import React, { useEffect, useRef, useState } from "react";
import { Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Divider } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import TrendExplanationDetail, { formatScore, SeverityBadge } from "./TrendExplanationDetail";

const PAD_LEFT = 60;
const PAD_RIGHT = 20;
const PAD_TOP = 16;
const PAD_BOTTOM = 40;
const PLOT_W = 780;
const CHART_W = PAD_LEFT + PLOT_W + PAD_RIGHT;

// Vertical scale is fixed to a real pixel-per-0.1-score ratio, not a fixed
// chart height squeezed to fit — so a 0.1 move (e.g. -2.3 to -2.4) is always
// a visible, consistent gap instead of getting flattened into invisibility.
const PX_PER_TENTH = 6; // px per 0.1 score unit
const PLOT_H = PX_PER_TENTH * 10 * 6; // 6 = full -3..+3 range in whole units
const CHART_H = PLOT_H + PAD_TOP + PAD_BOTTOM;

const TOOLTIP_W = 215;
// Max distance (viewBox px) a click/hover can be from an explainable point's
// center and still count as "on" it — lets us pick the nearest qualifying
// point by distance instead of relying on overlapping per-dot hit circles,
// which break down when two explainable points land close together (e.g.
// Overall and a topic both near 0 on the same date) — whichever dot's <g>
// happened to render last would silently eat the other's clicks.
const EXPLAIN_SNAP_PX = 16;
// Same idea, more forgiving radius — how close the cursor needs to be to any
// (not just explainable) point to count as "hovering that line", driving the
// same isolate-the-others highlight the legend already triggers on hover.
const LINE_HOVER_SNAP_PX = 22;

function scoreToY(score) {
  // score range -3..3 maps to plot top..bottom
  return PAD_TOP + ((3 - score) / 6) * PLOT_H;
}

// Smooth, monotone-in-x cubic spline through `points` ([x,y] pairs) — the
// same interpolation Recharts' type="monotone" (used by the home screen's
// MonitoringChart.js) builds via d3-shape's curveMonotoneX. Hand-rolled here
// since this chart is plain SVG, not Recharts. Deliberately NOT a naive
// Catmull-Rom spline: that can overshoot between two points and visually
// imply a bump/dip that isn't actually in the data. Monotone (Fritsch-
// Carlson) interpolation never does — the curve stays between each pair of
// points' y-values, so it only ever smooths the line, never misrepresents it.
function monotoneCubicPath(points) {
  const n = points.length;
  if (n === 0) return "";
  if (n === 1) return `M ${points[0][0]} ${points[0][1]}`;

  const xs = points.map((p) => p[0]);
  const ys = points.map((p) => p[1]);
  if (n === 2) return `M ${xs[0]} ${ys[0]} L ${xs[1]} ${ys[1]}`;

  const d = [];
  for (let i = 0; i < n - 1; i++) d.push((ys[i + 1] - ys[i]) / (xs[i + 1] - xs[i]));

  const m = new Array(n);
  m[0] = d[0];
  m[n - 1] = d[n - 2];
  for (let i = 1; i < n - 1; i++) {
    m[i] = d[i - 1] === 0 || d[i] === 0 || (d[i - 1] < 0) !== (d[i] < 0) ? 0 : (d[i - 1] + d[i]) / 2;
  }
  for (let i = 0; i < n - 1; i++) {
    if (d[i] === 0) { m[i] = 0; m[i + 1] = 0; continue; }
    const alpha = m[i] / d[i];
    const beta = m[i + 1] / d[i];
    const h = Math.hypot(alpha, beta);
    if (h > 3) {
      const tau = 3 / h;
      m[i] = tau * alpha * d[i];
      m[i + 1] = tau * beta * d[i];
    }
  }

  let path = `M ${xs[0]} ${ys[0]}`;
  for (let i = 0; i < n - 1; i++) {
    const dx = (xs[i + 1] - xs[i]) / 3;
    const p1x = xs[i] + dx, p1y = ys[i] + m[i] * dx;
    const p2x = xs[i + 1] - dx, p2y = ys[i + 1] - m[i + 1] * dx;
    path += ` C ${p1x} ${p1y} ${p2x} ${p2y} ${xs[i + 1]} ${ys[i + 1]}`;
  }
  return path;
}

// Draws the line in via a stroke-dashoffset sweep — matches Recharts'
// default Line animation (the home screen's MonitoringChart.js never
// disables it, so its lines draw in on mount/data-change the same way).
// Keyed on `d` alone, so hover-driven opacity/strokeWidth changes never
// retrigger it — only an actual data change (a genuinely different path)
// does.
function AnimatedPath({ d, ...props }) {
  const pathRef = useRef(null);
  useEffect(() => {
    const el = pathRef.current;
    if (!el) return;
    const length = el.getTotalLength();
    el.style.transition = "none";
    el.style.strokeDasharray = `${length}`;
    el.style.strokeDashoffset = `${length}`;
    el.getBoundingClientRect(); // force reflow so the reset above is committed first
    el.style.transition = "stroke-dashoffset 1400ms ease";
    el.style.strokeDashoffset = "0";
  }, [d]);
  return <path ref={pathRef} d={d} {...props} />;
}

/**
 * Plain SVG line chart — every series (topic buckets + an "Overall"
 * aggregate, styled distinctly) plotted across all available runs (months).
 * Y-axis fixed to -3..+3 with a zero reference line.
 *
 * Hovering anywhere over the plot shows a crosshair + a tooltip listing
 * every visible line's value at that date in one place (no need to hunt
 * point by point). Hovering near a specific line — in the chart itself, or
 * its legend entry below — isolates it instead (thickened, full opacity,
 * every other line faded to 15%) and swaps the tooltip to that one line's
 * score across every date, since that's more useful once a line is singled
 * out than the single-date view. The "Show / hide topics" row doubles as a
 * visibility control: click an entry to toggle its line, double-click to
 * solo it (hide every other line), or use the Show all / Hide all links for
 * everything at once. Points whose latest move crosses the same threshold
 * trendExplanation.js uses to generate an explanation get a louder
 * double-ring marker — click the point for the full "why".
 */
export default function TrendChart({ series, months }) {
  const n = months.length;
  const xFor = (i) => PAD_LEFT + (n > 1 ? (i / (n - 1)) * PLOT_W : PLOT_W / 2);

  const svgRef = useRef(null);
  const [activeExplanation, setActiveExplanation] = useState(null); // { label, fromMonth, toMonth, explanation }
  const [hiddenKeys, setHiddenKeys] = useState(() => new Set());
  const [hoveredKey, setHoveredKey] = useState(null);
  const [crosshairIndex, setCrosshairIndex] = useState(null);
  const [nearExplainable, setNearExplainable] = useState(false);

  const handlePointClick = (s, i) => {
    const explanation = s.explanations?.[i];
    if (!explanation) return; // not an explainable point — no-op
    setActiveExplanation({
      label: s.label,
      fromMonth: months[i - 1],
      toMonth: months[i],
      explanation,
    });
  };
  const handleDialogClose = () => setActiveExplanation(null);

  const toggleHidden = (key) => {
    setHiddenKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const showAll = () => setHiddenKeys(new Set());
  const hideAll = () => setHiddenKeys(new Set(series.map((s) => s.key)));
  const soloSeries = (key) => setHiddenKeys(new Set(series.filter((s) => s.key !== key).map((s) => s.key)));

  // A genuine double-click arrives as click, click, dblclick in quick
  // succession — without this, the two single clicks would toggle the line
  // off then back on (a visible flicker) before the solo actually applies.
  // Delaying the single-click's effect by the standard double-click window
  // lets a following dblclick cancel it outright instead.
  const pendingClickRef = useRef(null); // { key, timer }
  const handleChipClick = (key) => {
    if (pendingClickRef.current) clearTimeout(pendingClickRef.current.timer);
    const timer = setTimeout(() => {
      toggleHidden(key);
      pendingClickRef.current = null;
    }, 220);
    pendingClickRef.current = { key, timer };
  };
  const handleChipDoubleClick = (key) => {
    if (pendingClickRef.current) {
      clearTimeout(pendingClickRef.current.timer);
      pendingClickRef.current = null;
    }
    soloSeries(key);
  };

  const visibleSeries = series.filter((s) => !hiddenKeys.has(s.key));
  const gridScores = [3, 1.5, 0, -1.5, -3];

  // Converts a mouse event into viewBox coordinates (the <svg> renders at
  // 100% width, so screen px != viewBox units once the container is wider
  // or narrower than CHART_W).
  const toViewBoxCoords = (e) => {
    const svgEl = svgRef.current;
    if (!svgEl) return null;
    const rect = svgEl.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    return {
      x: (e.clientX - rect.left) * (CHART_W / rect.width),
      y: (e.clientY - rect.top) * (CHART_H / rect.height),
    };
  };

  const indexFromX = (x) => {
    const step = n > 1 ? PLOT_W / (n - 1) : PLOT_W;
    return Math.max(0, Math.min(n - 1, Math.round((x - PAD_LEFT) / step)));
  };

  // Nearest explainable point to (idx, y) within EXPLAIN_SNAP_PX, or null.
  // Distance-based so overlapping points resolve to "whichever is actually
  // closest to the cursor" instead of z-order.
  const nearestExplainableAt = (idx, y) => {
    let best = null;
    let bestDist = Infinity;
    visibleSeries.forEach((s) => {
      const p = s.points?.[idx];
      if (typeof p !== "number" || !s.explanations?.[idx]) return;
      const dist = Math.abs(scoreToY(p) - y);
      if (dist < bestDist) { bestDist = dist; best = s; }
    });
    return bestDist <= EXPLAIN_SNAP_PX ? best : null;
  };

  // Nearest point to (idx, y) across every visible series (not just
  // explainable ones) — drives hover-to-highlight when the cursor is near a
  // line in the chart itself, the same way hovering the legend already does.
  const nearestVisibleSeriesAt = (idx, y) => {
    let best = null;
    let bestDist = Infinity;
    visibleSeries.forEach((s) => {
      const p = s.points?.[idx];
      if (typeof p !== "number") return;
      const dist = Math.abs(scoreToY(p) - y);
      if (dist < bestDist) { bestDist = dist; best = s; }
    });
    return bestDist <= LINE_HOVER_SNAP_PX ? best : null;
  };

  const handlePlotMouseMove = (e) => {
    if (n === 0) return;
    const coords = toViewBoxCoords(e);
    if (!coords) return;
    const idx = indexFromX(coords.x);
    setCrosshairIndex(idx);
    setNearExplainable(Boolean(nearestExplainableAt(idx, coords.y)));
    const nearSeries = nearestVisibleSeriesAt(idx, coords.y);
    setHoveredKey(nearSeries ? nearSeries.key : null);
  };

  const handlePlotClick = (e) => {
    if (n === 0) return;
    const coords = toViewBoxCoords(e);
    if (!coords) return;
    const idx = indexFromX(coords.x);
    const s = nearestExplainableAt(idx, coords.y);
    if (s) handlePointClick(s, idx);
  };

  const lastIdx = n - 1;

  // Default tooltip: every visible line's value at the hovered date.
  const tooltipRows = crosshairIndex === null
    ? []
    : visibleSeries
        .map((s) => ({ key: s.key, label: s.label, color: s.color, value: s.points[crosshairIndex], isOverall: s.isOverall }))
        .filter((r) => typeof r.value === "number")
        .sort((a, b) => b.value - a.value);

  // Once a line is isolated — hovering it directly or its legend entry — its
  // full date-range history is more useful than the single-date view, so it
  // takes over the tooltip while that line is highlighted.
  const hoveredSeries = hoveredKey && !hiddenKeys.has(hoveredKey) ? visibleSeries.find((s) => s.key === hoveredKey) : null;
  const historyRows = hoveredSeries
    ? months.map((m, i) => ({ month: m, value: hoveredSeries.points[i] })).filter((r) => typeof r.value === "number")
    : [];
  const showHistory = Boolean(hoveredSeries);
  const showAllSeries = !showHistory && crosshairIndex !== null;

  // Anchor to the cursor's date column when hovering the chart itself; fall
  // back to the line's last point when the hover came from the legend
  // instead (no x position of its own in that case).
  const anchorIdx = crosshairIndex !== null ? crosshairIndex : lastIdx;
  const anchorY = showHistory && typeof hoveredSeries.points[anchorIdx] === "number" ? scoreToY(hoveredSeries.points[anchorIdx]) : PAD_TOP;

  const tooltipRowCount = showHistory ? historyRows.length : tooltipRows.length;
  const tooltipH = (showHistory || showAllSeries) ? 34 + tooltipRowCount * 20 : 0;
  const tooltipX = anchorIdx > (n - 1) / 2 ? xFor(anchorIdx) - 14 - TOOLTIP_W : xFor(anchorIdx) + 14;
  const tooltipY = showHistory ? Math.max(PAD_TOP, Math.min(anchorY - tooltipH / 2, PAD_TOP + PLOT_H - tooltipH)) : PAD_TOP;

  return (
    <Box>
      {/* id is a first-time-tour anchor (OnboardingTour.js looks it up by DOM
          id) — just the plotted chart, not the title/legend/note around it,
          so step 1 highlights only the graph itself. */}
      <svg id="cm-tour-trend-chart" ref={svgRef} width="100%" viewBox={`0 0 ${CHART_W} ${CHART_H}`}>
        <line x1={PAD_LEFT} y1={PAD_TOP} x2={PAD_LEFT} y2={CHART_H - PAD_BOTTOM} stroke="#e4e7ec" strokeWidth="1" />
        {gridScores.map((s) => (
          <line
            key={s}
            x1={PAD_LEFT}
            y1={scoreToY(s)}
            x2={PAD_LEFT + PLOT_W}
            y2={scoreToY(s)}
            stroke={s === 0 ? "#c3c9d3" : "#f0f2f5"}
            strokeWidth={s === 0 ? 1.5 : 1}
            strokeDasharray={s === 0 ? "4 3" : undefined}
          />
        ))}

        <text x={PAD_LEFT - 10} y={scoreToY(3) + 4} textAnchor="end" fontSize="11" fill="#98a2b3">+3</text>
        <text x={PAD_LEFT - 10} y={scoreToY(0) + 4} textAnchor="end" fontSize="11" fill="#98a2b3">0</text>
        <text x={PAD_LEFT - 10} y={scoreToY(-3) + 4} textAnchor="end" fontSize="11" fill="#98a2b3">&minus;3</text>

        {months.map((m, i) => (
          <text key={m} x={xFor(i)} y={CHART_H - PAD_BOTTOM + 22} textAnchor="middle" fontSize="11.5" fill="#667085">
            {m}
          </text>
        ))}

        {/* Single surface owning all plot-area pointer input — hover shows
            the crosshair, click opens the explanation for whichever
            explainable point is nearest the cursor (see nearestExplainableAt).
            Dots below are pure visuals (pointerEvents: none) so this is the
            only thing ever doing hit-testing — no per-dot hit-circle overlap
            to fight over when two points land close together. */}
        <rect
          x={PAD_LEFT - 10}
          y={PAD_TOP - 10}
          width={PLOT_W + 20}
          height={PLOT_H + 20}
          fill="transparent"
          onMouseMove={handlePlotMouseMove}
          onMouseLeave={() => { setCrosshairIndex(null); setNearExplainable(false); setHoveredKey(null); }}
          onClick={handlePlotClick}
          style={{ cursor: nearExplainable ? "pointer" : "crosshair" }}
        />

        {visibleSeries.map((s) => {
          // Ignore a stale hover on a line that was just hidden via its own
          // legend click (cursor is still sitting on that chip) — otherwise
          // every remaining visible line would incorrectly dim as if some
          // other line were being isolated.
          const isHovered = hoveredKey === s.key;
          const isHoveringVisibleOther = hoveredKey && !isHovered && !hiddenKeys.has(hoveredKey);
          const opacity = isHoveringVisibleOther ? 0.15 : 1;
          const strokeWidth = (s.isOverall ? 3.5 : 2) + (isHovered ? 1.5 : 0);

          // Real data can have a null score for a run where this topic had
          // no scored items — split into contiguous non-null runs so a gap
          // shows up instead of a broken/false-zero line segment.
          const segments = [];
          let current = [];
          s.points.forEach((p, i) => {
            if (typeof p === "number") {
              current.push([xFor(i), scoreToY(p)]);
            } else if (current.length) {
              segments.push(current);
              current = [];
            }
          });
          if (current.length) segments.push(current);

          return (
            <g key={s.key} opacity={opacity} style={{ transition: "opacity 0.15s", pointerEvents: "none" }}>
              {/* Lines/dots are pure visuals — pointerEvents disabled here (and
                  cascading to every child) so a dot sitting exactly on its own
                  line's stroke never intercepts a click meant for the overlay
                  rect below; that rect is the only thing doing hit-testing. */}
              {/* White casing drawn first, then the real stroke on top — this is
                  what keeps "Overall" legible where several colored lines cross
                  right through it, instead of competing in the same visual
                  weight class as everything else. */}
              {s.isOverall && segments.map((seg, si) => (
                <AnimatedPath key={`casing-${si}`} d={monotoneCubicPath(seg)} fill="none" stroke="#ffffff" strokeWidth={strokeWidth + 3} strokeLinecap="round" strokeLinejoin="round" />
              ))}
              {segments.map((seg, si) => (
                <AnimatedPath
                  key={si}
                  d={monotoneCubicPath(seg)}
                  fill="none"
                  stroke={s.color}
                  strokeWidth={strokeWidth}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              ))}
              {s.points.map((p, i) => {
                if (typeof p !== "number") return null;
                const isExplainable = Boolean(s.explanations?.[i]);
                const r = s.isOverall ? 5 : 3.5;
                const cx = xFor(i);
                const cy = scoreToY(p);
                return (
                  <g key={i} style={{ pointerEvents: "none" }}>
                    {isExplainable && <circle cx={cx} cy={cy} r={9} fill="#ffffff" stroke={s.color} strokeWidth={2} />}
                    <circle cx={cx} cy={cy} r={isExplainable ? r + 1 : r} fill={s.color} />
                  </g>
                );
              })}
            </g>
          );
        })}

        {crosshairIndex !== null && (
          // pointerEvents: none is load-bearing here — this line tracks the
          // cursor's own x position, so without it the line would end up
          // sitting exactly under the cursor and intercept the very next
          // mousemove, firing the overlay rect's onMouseLeave and resetting
          // the crosshair/highlight to null on every other frame.
          <line x1={xFor(crosshairIndex)} y1={PAD_TOP} x2={xFor(crosshairIndex)} y2={PAD_TOP + PLOT_H} stroke="#98a2b3" strokeWidth={1} strokeDasharray="3 3" style={{ pointerEvents: "none" }} />
        )}

        {(showHistory || showAllSeries) && (
          <foreignObject x={tooltipX} y={tooltipY} width={TOOLTIP_W} height={tooltipH} style={{ overflow: "visible", pointerEvents: "none" }}>
            <div
              xmlns="http://www.w3.org/1999/xhtml"
              style={{
                background: "#1a2027",
                borderRadius: 8,
                padding: "8px 12px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.18)",
                fontFamily: "inherit",
              }}
            >
              {showHistory ? (
                <>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, fontWeight: 700, color: "#fff", marginBottom: 6 }}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: hoveredSeries.color, flexShrink: 0 }} />
                    {hoveredSeries.label}
                  </div>
                  {historyRows.map((r) => (
                    <div key={r.month} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 11.5, color: "#d0d5dd", padding: "1.5px 0" }}>
                      <span style={{ flex: 1 }}>{r.month}</span>
                      <span style={{ fontWeight: 700, color: "#fff" }}>{formatScore(r.value)}</span>
                    </div>
                  ))}
                </>
              ) : (
                <>
                  <div style={{ fontSize: 11.5, fontWeight: 700, color: "#fff", marginBottom: 6 }}>{months[crosshairIndex]}</div>
                  {tooltipRows.map((r) => (
                    <div key={r.key} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, color: "#d0d5dd", padding: "1.5px 0", fontWeight: r.isOverall ? 700 : 400 }}>
                      <span style={{ width: 8, height: 8, borderRadius: "50%", background: r.color, flexShrink: 0 }} />
                      <span style={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: r.isOverall ? "#fff" : "#d0d5dd" }}>{r.label}</span>
                      <span style={{ fontWeight: 700, color: "#fff" }}>{formatScore(r.value)}</span>
                    </div>
                  ))}
                </>
              )}
            </div>
          </foreignObject>
        )}
      </svg>

      <Divider sx={{ mt: 1.5, mb: 1.5 }} />

      {/* id is a first-time-tour anchor (OnboardingTour.js looks it up by
          DOM id) — wraps both the header row and the chip row so the
          highlight covers the whole legend area, not just the heading. */}
      <Box id="cm-tour-legend">
        <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: 1, pl: `${PAD_LEFT}px`, pr: 2 }}>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap" }}>
            <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: "#1a2027" }}>Show / hide topics</Typography>
            <Typography sx={{ fontSize: 12, color: "#667085" }}>
              Click a topic to toggle its line. Double-click to show only that one.
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1.5, flexShrink: 0 }}>
            <Typography onClick={showAll} sx={{ fontSize: 12.5, fontWeight: 600, color: "#1976d2", cursor: "pointer", "&:hover": { textDecoration: "underline" } }}>
              Show all
            </Typography>
            <Typography onClick={hideAll} sx={{ fontSize: 12.5, fontWeight: 600, color: "#1976d2", cursor: "pointer", "&:hover": { textDecoration: "underline" } }}>
              Hide all
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1.25, pl: `${PAD_LEFT}px` }}>
          {series.map((s) => {
          const isHidden = hiddenKeys.has(s.key);
          return (
            // Tinted with the series' own color — not just a neutral bordered
            // chip — so each one visually ties back to its line, same as a
            // selected filter chip would. The eye icon spells out the exact
            // action; double-click soloing mirrors the standard "isolate a
            // series" gesture common to most charting UIs.
            <Box
              key={s.key}
              onClick={() => handleChipClick(s.key)}
              onDoubleClick={() => handleChipDoubleClick(s.key)}
              onMouseEnter={() => setHoveredKey(s.key)}
              onMouseLeave={() => setHoveredKey(null)}
              title={isHidden ? `Show ${s.label}` : `Hide ${s.label} (double-click to show only this one)`}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.6,
                fontSize: 12.5,
                fontWeight: 700,
                color: isHidden ? "#98a2b3" : "#1a2027",
                cursor: "pointer",
                userSelect: "none",
                px: 1.1,
                py: 0.5,
                borderRadius: "14px",
                border: "1.5px solid",
                borderColor: isHidden ? "#eef1f4" : `${s.color}66`,
                bgcolor: isHidden ? "#fafbfc" : `${s.color}1f`,
                opacity: isHidden ? 0.6 : 1,
                transition: "background-color 0.12s, border-color 0.12s, opacity 0.12s",
                "&:hover": { borderColor: isHidden ? "#d0d5dd" : `${s.color}99` },
              }}
            >
              <Box sx={{ width: 9, height: 9, borderRadius: s.isOverall ? "2px" : "50%", bgcolor: isHidden ? "#d0d5dd" : s.color, flexShrink: 0 }} />
              {isHidden ? (
                <VisibilityOffOutlinedIcon sx={{ fontSize: 14, color: "#b4bac4" }} />
              ) : (
                <VisibilityOutlinedIcon sx={{ fontSize: 14, color: "#475467" }} />
              )}
              {s.label}
            </Box>
          );
        })}
        </Box>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 1.5, pl: `${PAD_LEFT}px` }}>
        <svg width="14" height="14" style={{ flexShrink: 0 }}>
          <circle cx="7" cy="7" r="6" fill="#ffffff" stroke="#98a2b3" strokeWidth="2" />
          <circle cx="7" cy="7" r="3" fill="#98a2b3" />
        </svg>
        <Typography sx={{ fontSize: 11.5, color: "#667085" }}>
          Ringed point: a significant move (&plusmn;0.5 or more since the previous run). Click it to see why.
        </Typography>
      </Box>

      <Dialog open={Boolean(activeExplanation)} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        {activeExplanation && (() => {
          const exp = activeExplanation.explanation;
          return (
            <>
              <DialogTitle sx={{ pb: 0.5 }}>
                <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#1a2027" }}>
                  {activeExplanation.label}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                  <Typography sx={{ fontSize: 12.5, color: "#667085" }}>
                    {activeExplanation.fromMonth} → {activeExplanation.toMonth}: {formatScore(exp.fromScore)} → {formatScore(exp.toScore)}
                  </Typography>
                  <SeverityBadge fromScore={exp.fromScore} toScore={exp.toScore} />
                </Box>
              </DialogTitle>
              <Divider />
              <DialogContent>
                <TrendExplanationDetail explanation={exp} />
              </DialogContent>
              <DialogActions>
                <Button onClick={handleDialogClose}>Close</Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>
    </Box>
  );
}
